/**
 * The navigation, once.
 *
 * The prototype originally had eight hand-written buttons — a sidebar set and a bottom-bar set —
 * with the icons pasted into both. Any new destination had to be added twice or the two drifted.
 * This array is the fix, and it is why `Sidebar` and `BottomNav` cannot disagree: they map the
 * same list. Add a destination here and both navigations get it.
 *
 * `label` is the sidebar's wording, `short` the bottom bar's. They differ on purpose — "Dog
 * profile" does not fit a quarter of a phone's width.
 */

import type { NavIconId } from './icons'

/**
 * Every screen. A literal union, so an unknown screen is unrepresentable.
 *
 * `privacy` is deliberately NOT in `NAV_ITEMS` below. It is a destination the app can navigate to,
 * but it is not one of the four places a client moves between — putting it in the array would give
 * it a fifth slot in the bottom bar, where the grid is already at the limit of a 320px screen, and
 * would rank a legal notice alongside the dog's own record. It is reached from the sidebar footer
 * and, signed out, from the sign-in screen.
 */
export type ScreenId = 'home' | 'profile' | 'reminders' | 'review' | 'privacy'

export interface NavItem {
  readonly id: ScreenId
  /** Sidebar wording. */
  readonly label: string
  /** Bottom-bar wording — must stay short enough for a 4-column grid on a 320px screen. */
  readonly short: string
  readonly icon: NavIconId
}

export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'home', label: 'Home', short: 'Home', icon: 'home' },
  { id: 'profile', label: 'Dog profile', short: 'Profile', icon: 'paw' },
  { id: 'reminders', label: 'Reminders', short: 'Reminders', icon: 'bell' },
  { id: 'review', label: 'Review details', short: 'Review', icon: 'clipboard' },
] as const

/**
 * The presentation of one nav item in one of the two navigations.
 *
 * `current` is a STRING because `aria-current` is a token attribute, not a boolean: React drops an
 * attribute whose value is literal `false`, which would silently remove the "you are here"
 * announcement for a screen-reader user. `badge` is a real boolean — it only ever gates whether a
 * dot element exists.
 */
export interface NavItemView extends NavItem {
  readonly active: boolean
  readonly current: 'page' | 'false'
  readonly background: string
  readonly foreground: string
  readonly badge: boolean
}

/**
 * Build both navigations' view models from the current screen.
 *
 * `unreviewed` puts the attention dot on "Review details". In the prototype that flag was the
 * wizard's own `done` latch; against real data it should be driven by whatever the record says
 * about the outstanding review, not by a local UI flag that resets on reload.
 */
export function buildNavItems(screen: ScreenId, unreviewed: boolean): NavItemView[] {
  return NAV_ITEMS.map((item) => {
    const active = screen === item.id
    return {
      ...item,
      active,
      current: active ? 'page' : 'false',
      background: active ? 'var(--color-accent-100)' : 'transparent',
      foreground: active ? 'var(--color-accent-800)' : 'var(--color-neutral-700)',
      badge: item.id === 'review' && unreviewed,
    }
  })
}
